/**
 * Shopify Forms Styles Manager
 * Applies custom CSS to Shopify Forms elements
 * - Targets Shadow DOM within shopify-forms-embed
 * - Applies globally if shopify-app-block exists
 */
class ShopifyFormsStyles {
	constructor() {
		this.termsConditionsMessage = window.ShopifyFormsConfig?.termsConditionsMessage || null;
		this.init();
	}

	/**
	 * Checks if shopify-app-block exists in the DOM
	 */
	hasShopifyAppBlock() {
		return document.querySelector('.shopify-app-block') !== null;
	}

	/**
	 * Returns the CSS rules for form styling
	 */
	getCSSRules() {
		return `
			shopify-forms-embed label._formInputFieldLabel_1mxsl_38,
			shopify-forms-embed label[class*="_formInputFieldLabel"],
			shopify-forms-embed label[class*="formInputFieldLabel"] {
				top: 14px !important;
			}

			shopify-forms-embed	button[class*="_selectToggle"],
			shopify-forms-embed input,
			shopify-forms-embed textarea {
				border-radius: 0 !important;
				font-family: 'Aries Ranging Figs', sans-serif !important;
			}

			shopify-forms-embed button[class*="_formSubmitButton"] {
				font-family: 'avenir-lt-pro', sans-serif !important;
				text-transform: uppercase !important;
				border-radius: 50px !important;
			}
		`;
	}

	/**
	 * Returns CSS rules for Shadow DOM (without shopify-forms-embed prefix)
	 */
	getShadowCSSRules() {
		return `
			label._formInputFieldLabel_1mxsl_38,
			label[class*="_formInputFieldLabel"],
			label[class*="formInputFieldLabel"] {
				top: 14px !important;
			}

			button[class*="_selectToggle"],
			input,
			textarea {
				border-radius: 0 !important;
				font-family: 'Aries Ranging Figs', sans-serif !important;
			}

			button[class*="_formSubmitButton"] {
				font-family: 'avenir-lt-pro', sans-serif !important;
				text-transform: uppercase !important;
				border-radius: 50px !important;
				background: transparent !important;
				border-color: #8C7A66 !important;
				color: #8C7A66 !important;
				border: 1px solid !important;
			}
			button[class*="_formSubmitButton"]:hover {
				background-color: transparent !important;
				color: #AFA294 !important;
				border-color: #AFA294 !important;
			}
		`;
	}

	/**
	 * Injects CSS globally to document head
	 */
	injectGlobalCSS() {
		// Check if already injected
		if (document.querySelector('[data-shopify-forms-global]')) {
			console.log('ℹ️ Global form CSS already injected');
			return;
		}

		const styleEl = document.createElement('style');
		styleEl.textContent = this.getCSSRules();
		styleEl.setAttribute('data-shopify-forms-global', 'true');
		document.head.appendChild(styleEl);
		console.log('✅ Injected global form CSS into document head');
	}

	/**
	 * Injects CSS into Shadow DOM
	 */
	injectShadowCSS(shadowRoot) {
		// Check if already injected
		if (shadowRoot.querySelector('[data-shopify-forms-shadow]')) {
			console.log('ℹ️ Shadow DOM CSS already injected');
			return;
		}

		const styleEl = document.createElement('style');
		styleEl.textContent = this.getShadowCSSRules();
		styleEl.setAttribute('data-shopify-forms-shadow', 'true');
		shadowRoot.appendChild(styleEl);
		console.log('✅ Injected form CSS into Shadow DOM');
	}

	/**
	 * Applies inline styles to labels
	 */
	styleLabels(container) {
		const labels = container.querySelectorAll(
			'label._formInputFieldLabel_1mxsl_38, label[class*="_formInputFieldLabel"], label[class*="formInputFieldLabel"]'
		);

		labels.forEach(label => {
			label.style.top = '14px';
		});

		if (labels.length > 0) {
			const context = container === document ? 'globally' : 'in container';
			console.log(`✅ Styled ${labels.length} label(s) ${context}`);
		}

		return labels;
	}

	/**
	 * Applies inline styles to form elements (buttons, inputs, textareas)
	 */
	styleFormElements(container) {
		const elements = container.querySelectorAll('button, input, textarea');
		let regularCount = 0;
		let submitCount = 0;

		elements.forEach(element => {
			// Check if it's a submit button with _formSubmitButton class
			const isSubmitButton = element.tagName === 'BUTTON' &&
				Array.from(element.classList).some(cls => cls.includes('_formSubmitButton'));

			if (isSubmitButton) {
				element.style.fontFamily = "'avenir-lt-pro', sans-serif";
				element.style.textTransform = 'uppercase';
				element.style.borderRadius = '50px';
				submitCount++;
			} else {
				element.style.borderRadius = '0';
				element.style.fontFamily = "'Aries Ranging Figs', sans-serif";
				regularCount++;
			}
		});

		if (regularCount > 0 || submitCount > 0) {
			const context = container === document ? 'globally' : 'in container';
			console.log(`✅ Styled ${regularCount} regular element(s) and ${submitCount} submit button(s) ${context}`);
		}

		return elements;
	}

	/**
	 * Updates disclaimer text with termsConditionsMessage
	 */
	updateDisclaimers(container) {
		if (!this.termsConditionsMessage) {
			return;
		}

		const disclaimers = container.querySelectorAll('[class*="_formDisclaimer"] p');

		disclaimers.forEach((disclaimer, index) => {
			disclaimer.innerHTML = this.termsConditionsMessage;
		});

		if (disclaimers.length > 0) {
			const context = container === document ? 'globally' : 'in container';
			console.log(`✅ Updated ${disclaimers.length} disclaimer(s) ${context}`);
		}

		return disclaimers;
	}

	/**
	 * Observes labels and prevents them from moving
	 */
	observeLabels(container) {
		const labels = container.querySelectorAll(
			'label._formInputFieldLabel_1mxsl_38, label[class*="_formInputFieldLabel"], label[class*="formInputFieldLabel"]'
		);

		labels.forEach(label => {
			const observer = new MutationObserver(() => {
				if (label.style.top && label.style.top !== '14px') {
					label.style.top = '14px';
				}
			});

			observer.observe(label, {
				attributes: true,
				attributeFilter: ['style']
			});

			// Clean up after 5 seconds
			setTimeout(() => observer.disconnect(), 5000);
		});

		if (labels.length > 0) {
			const context = container === document ? 'globally' : 'in container';
			console.log(`👀 Observing ${labels.length} label(s) for position changes ${context}`);
		}
	}

	/**
	 * Applies all styles to a container (Shadow DOM or specific element)
	 */
	applyStyles(container) {
		this.styleLabels(container);
		this.styleFormElements(container);
		this.updateDisclaimers(container);
		this.observeLabels(container);
	}

	/**
	 * Applies styles to Shadow DOM
	 */
	applyShadowStyles(shadowRoot) {
		console.log('🌓 Applying Shadow DOM form styles');
		this.injectShadowCSS(shadowRoot);
		this.applyStyles(shadowRoot);
	}

	/**
	 * Handles styling for a shopify-forms-embed element
	 */
	handleFormsEmbed(formsEmbed) {
		// Try multiple times as Shadow DOM might not be ready
		const attemptStyling = (attempt = 0) => {
			if (attempt > 5) return;

			if (formsEmbed.shadowRoot) {
				this.applyShadowStyles(formsEmbed.shadowRoot);
			} else {
				// If no Shadow DOM, style the element's content directly
				console.log('📝 No Shadow DOM found, styling regular DOM');
				this.applyStyles(formsEmbed);
			}

			// Try again after a delay (only if shadowRoot wasn't found yet)
			if (!formsEmbed.shadowRoot) {
				setTimeout(() => attemptStyling(attempt + 1), 200 * (attempt + 1));
			}
		};

		attemptStyling();
	}

	/**
	 * Observes for shopify-forms-embed elements and styles their Shadow DOM
	 */
	observeFormsEmbedElements() {
		const observer = new MutationObserver((mutations) => {
			for (const mutation of mutations) {
				if (mutation.addedNodes.length) {
					mutation.addedNodes.forEach(node => {
						if (node.nodeType === 1) {
							// Check if the node itself is shopify-forms-embed
							if (node.tagName === 'SHOPIFY-FORMS-EMBED') {
								console.log('🆕 shopify-forms-embed detected');
								this.handleFormsEmbed(node);
							}
							// Or if it contains shopify-forms-embed
							const embed = node.querySelector ? node.querySelector('shopify-forms-embed') : null;
							if (embed) {
								console.log('🆕 shopify-forms-embed detected inside added node');
								this.handleFormsEmbed(embed);
							}
						}
					});
				}
			}
		});

		observer.observe(document.body, {
			childList: true,
			subtree: true
		});

		console.log('👀 Observing for shopify-forms-embed elements');
	}

	/**
	 * Initializes and applies styles on existing forms
	 */
	styleExistingForms() {
		// Style any existing shopify-forms-embed elements
		const existingForms = document.querySelectorAll('shopify-forms-embed');

		if (existingForms.length > 0) {
			console.log(`📋 Found ${existingForms.length} existing form(s)`);
			existingForms.forEach(form => this.handleFormsEmbed(form));
		}
	}

	init() {
		console.log('🎨 ShopifyFormsStyles initialized');
		console.log('📋 Terms & Conditions message:', this.termsConditionsMessage);

		// Check if we should inject global CSS
		if (this.hasShopifyAppBlock()) {
			console.log('🌐 shopify-app-block detected - injecting global CSS');
			// Only inject CSS rules, don't apply inline styles globally
			this.injectGlobalCSS();
		}

		// Always watch for shopify-forms-embed elements
		this.observeFormsEmbedElements();

		// Style any existing forms
		this.styleExistingForms();
	}
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => new ShopifyFormsStyles());
} else {
	new ShopifyFormsStyles();
}
